import { useCallback, useEffect, useRef, useState } from 'react';

interface DragOrigin {
  /** Pointer coordinates when the drag started. */
  startX: number;
  startY: number;
  /** Offset already applied to the pill when the drag started. */
  originX: number;
  originY: number;
  /** Offset bounds (computed once at drag start) that keep the pill inside its container. */
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

interface DraggablePill {
  /** Translation applied on top of the pill's anchored base position. */
  offset: { x: number; y: number };
  /** Ref attached to the draggable element so bounds can be measured. */
  pillRef: React.RefObject<HTMLDivElement>;
  /** Spread onto the drag handle; no-ops when dragging is disabled. */
  dragHandleProps: { onPointerDown: (event: React.PointerEvent) => void };
  /** Whether a drag is in progress (drives the grab/grabbing cursor). */
  isDragging: boolean;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Makes an anchored, `position`-ed element draggable within its offset parent
 * (Zoom-style "grab the bar and move it anywhere"). The element keeps its CSS
 * anchor (e.g. bottom-center) and this hook layers a clamped `translate()` on
 * top, so the pill can never be dragged outside its container. Offset persists
 * across re-renders, so a dragged pill stays where the user left it.
 */
export const useDraggablePill = (enabled: boolean): DraggablePill => {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const pillRef = useRef<HTMLDivElement>(null);
  const dragOrigin = useRef<DragOrigin | null>(null);

  const handlePointerMove = useCallback((event: PointerEvent) => {
    const origin = dragOrigin.current;
    if (!origin) return;
    const nextX = clamp(origin.originX + (event.clientX - origin.startX), origin.minX, origin.maxX);
    const nextY = clamp(origin.originY + (event.clientY - origin.startY), origin.minY, origin.maxY);
    setOffset({ x: nextX, y: nextY });
  }, []);

  const handlePointerUp = useCallback(() => {
    dragOrigin.current = null;
    setIsDragging(false);
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
  }, [handlePointerMove]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      const element = pillRef.current;
      if (!enabled || !element || event.button !== 0) return;
      event.preventDefault();

      const rect = element.getBoundingClientRect();
      // The element's rect already includes the current offset, so subtract it
      // to recover the un-translated anchor position, then derive the offset
      // range that keeps the pill fully inside its bounding container.
      const baseLeft = rect.left - offset.x;
      const baseTop = rect.top - offset.y;

      const parent = element.offsetParent as HTMLElement | null;
      const bounds = parent
        ? parent.getBoundingClientRect()
        : { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };

      dragOrigin.current = {
        startX: event.clientX,
        startY: event.clientY,
        originX: offset.x,
        originY: offset.y,
        minX: bounds.left - baseLeft,
        maxX: bounds.right - rect.width - baseLeft,
        minY: bounds.top - baseTop,
        maxY: bounds.bottom - rect.height - baseTop,
      };
      setIsDragging(true);
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    },
    [enabled, offset.x, offset.y, handlePointerMove, handlePointerUp]
  );

  // Safety net: detach window listeners if the pill unmounts mid-drag.
  useEffect(
    () => () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    },
    [handlePointerMove, handlePointerUp]
  );

  return { offset, pillRef, dragHandleProps: { onPointerDown }, isDragging };
};
